import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import { api } from 'api/v1/api';
import { isAuthSessionActive, useAuthSession } from 'hooks/useAuthSession';

import type { CaptureStepId } from 'modules/PassportCapture/types';

export type PassportPhotoStatus = 'pending_review' | 'approved' | 'rejected';

export type PassportVerificationPhoto = {
  number: number;
  token: string;
  url: string;
  status: PassportPhotoStatus;
  comment: string | null;
};

export type PassportReshoot = {
  required?: boolean;
  photos?: { number: number; step: CaptureStepId; label: string }[];
  message?: string | null;
};

export type PassportVerificationStatus = {
  id?: number;
  status: PassportPhotoStatus | 'not_submitted';
  profile_verified: boolean;
  photos?: PassportVerificationPhoto[];
  photos_needing_resubmit?: number[];
  passport_data?: PassportOcrData;
  reshoot?: PassportReshoot | null;
  message?: string;
  created_at?: string;
  updated_at?: string;
  reviewed_at?: string | null;
  moderation_expires_at?: string | null;
  photos_purged?: boolean;
};

export type PassportVerifyPayload = Partial<Record<CaptureStepId, Blob>>;

export type PassportOcrData = {
  available?: boolean;
  readable?: boolean;
  readability?: {
    readable?: boolean;
    message?: string;
    profile_fields_checked?: boolean;
    text_snippets?: string[];
    text_readability?: {
      readable?: boolean;
      snippets?: string[];
    };
    checked_fields?: Array<{
      key: string;
      label: string;
      recognized: string;
      expected?: string;
      matched?: boolean;
    }>;
  };
  fields?: Record<string, unknown> | null;
  validation?: {
    field_validity_score?: number;
    all_required_present?: boolean;
    issues?: string[];
  } | null;
  mrz?: unknown;
  engine?: string;
  message?: string;
  ocr?: Record<string, unknown>;
};

const PASSPORT_STATUS_KEY = ['passport-verification-status'] as const;

const STEP_TO_FIELD: Record<CaptureStepId, 'photo1' | 'photo2' | 'photo3'> = {
  spread: 'photo1',
  registration: 'photo2',
  selfie: 'photo3',
};

function blobToUploadFile(blob: Blob, fieldName: string): File {
  const extension = blob.type === 'image/png' ? 'png' : 'jpg';
  return new File([blob], `${fieldName}.${extension}`, {
    type: blob.type || 'image/jpeg',
  });
}

async function fetchPassportStatus(): Promise<PassportVerificationStatus | null> {
  try {
    const response = await api.get<PassportVerificationStatus>('/passport/status');
    if (response.data?.status === 'not_submitted') {
      return null;
    }
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

export function usePassportVerificationStatus() {
  const { data: session } = useAuthSession();

  return useQuery({
    queryKey: PASSPORT_STATUS_KEY,
    queryFn: fetchPassportStatus,
    enabled: isAuthSessionActive(session),
    staleTime: 30_000,
    retry: false,
  });
}

export function useSubmitPassportVerify() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: Required<Record<CaptureStepId, Blob>>) => {
      const formData = new FormData();

      (Object.entries(STEP_TO_FIELD) as [CaptureStepId, 'photo1' | 'photo2' | 'photo3'][]).forEach(
        ([stepId, fieldName]) => {
          const blob = payload[stepId];
          if (!blob) {
            throw new Error(`Missing photo for step: ${stepId}`);
          }
          formData.append(fieldName, blobToUploadFile(blob, fieldName));
        },
      );

      const response = await api.post<PassportVerificationStatus>('/passport/verify', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(PASSPORT_STATUS_KEY, data);
      queryClient.invalidateQueries({ queryKey: ['profile', 'tutor'] });
    },
  });
}

export function useResubmitPassportPhotos() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: PassportVerifyPayload) => {
      const formData = new FormData();
      let count = 0;

      (Object.entries(STEP_TO_FIELD) as [CaptureStepId, 'photo1' | 'photo2' | 'photo3'][]).forEach(
        ([stepId, fieldName]) => {
          const blob = payload[stepId];
          if (!blob) {
            return;
          }
          count += 1;
          formData.append(fieldName, blobToUploadFile(blob, fieldName));
        },
      );

      if (count === 0) {
        throw new Error('Нет фото для пересдачи');
      }

      const response = await api.post<PassportVerificationStatus>(
        '/passport/verify/resubmit',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      );

      return response.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(PASSPORT_STATUS_KEY, data);
      queryClient.invalidateQueries({ queryKey: ['profile', 'tutor'] });
    },
  });
}

export function usePassportOcrPreview() {
  return useMutation({
    mutationFn: async ({ photo, photoNumber = 1 }: { photo: Blob; photoNumber?: number }) => {
      const formData = new FormData();
      formData.append('photo', blobToUploadFile(photo, 'passport-ocr-preview'));
      formData.append('photo_number', String(photoNumber));

      const response = await api.post<PassportOcrData>('/passport/ocr-preview', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data;
    },
  });
}
